import React from "react";
import Select, { components as selectComponents } from "react-select";
import Datetime from "react-datetime";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import ButtonLoader from "../ButtonLoader/ButtonLoader";
import strings from "../../core/translate";
import "react-datetime/css/react-datetime.css";

// Shared building blocks for the garage forms: stacked labels, one field per
// block, inline errors under the control.

export const FormSection = ({ title, icon, children }) => (
  <div className="mform__section">
    {title && (
      <h6 className="mform__section-title">
        {icon && <i className={icon} />}
        {title}
      </h6>
    )}
    {children}
  </div>
);

export const FormGrid = ({ children, full = false }) => (
  <div className={`mform__grid${full ? " mform__grid--full" : ""}`}>
    {children}
  </div>
);

export const Field = ({ label, required, help, error, children }) => (
  <div className={`mfield${error ? " mfield--invalid" : ""}`}>
    {label && (
      <label className="mfield__label">
        {label}
        {required && <span className="mfield__req">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <span className="mfield__error">{error}</span>
    ) : (
      help && <span className="mfield__help">{help}</span>
    )}
  </div>
);

export const TextField = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  help,
  error,
  disabled = false,
  step,
  min,
  inputMode,
}) => (
  <Field label={label} required={required} help={help} error={error}>
    {type === "textarea" ? (
      <textarea
        value={value === null || value === undefined ? "" : value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        type={type}
        value={value === null || value === undefined ? "" : value}
        placeholder={placeholder}
        disabled={disabled}
        step={step}
        min={min}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </Field>
);

// The row that takes the whole list in one click. It is never part of the
// value, it only ever reaches the change handler.
const SELECT_ALL = "__select_all__";

/**
 * Every option picked would otherwise print one chip per option and push the
 * rest of the form off the screen, so the whole selection collapses into a
 * single chip. Only the first value renders; its remove button drops the lot,
 * since removing one of them from here has no meaning.
 */
const AllSelectedValue = (props) =>
  props.index === 0 ? (
    <selectComponents.MultiValue
      {...props}
      removeProps={{
        onClick: props.selectProps.onClearAll,
        onTouchEnd: props.selectProps.onClearAll,
        onMouseDown: (event) => {
          event.preventDefault();
          event.stopPropagation();
        },
      }}
    >
      {props.selectProps.allSelectedLabel}
    </selectComponents.MultiValue>
  ) : null;

const ALL_SELECTED_COMPONENTS = { MultiValue: AllSelectedValue };

export const SelectField = ({
  label,
  value,
  options = [],
  onChange,
  isMulti = false,
  placeholder = strings.select,
  required = false,
  help,
  error,
  disabled = false,
  allowSelectAll = false,
}) => {
  const selected = isMulti
    ? (value || [])
        .map((item) =>
          options.find(
            (option) => option.value === (item && item.value ? item.value : item)
          )
        )
        .filter(Boolean)
    : options.find((option) => option.value === value) || null;

  // Once everything is picked the row has nothing left to add, so it turns
  // into the way back out.
  const allSelected =
    isMulti && options.length > 0 && selected.length === options.length;
  const shownOptions =
    allowSelectAll && isMulti && options.length > 0
      ? [
          {
            value: SELECT_ALL,
            label: allSelected ? strings.clear_all : strings.select_all,
          },
          ...options,
        ]
      : options;

  const handleChange = (event) => {
    if (!isMulti) {
      onChange(event ? event.value : "");
      return;
    }
    const picked = event || [];
    if (picked.some((item) => item.value === SELECT_ALL)) {
      onChange(allSelected ? [] : options.map((option) => option.value));
      return;
    }
    onChange(picked.map((item) => item.value));
  };

  return (
    <Field label={label} required={required} help={help} error={error}>
      <Select
        classNamePrefix="mselect"
        isMulti={isMulti}
        isDisabled={disabled}
        options={shownOptions}
        value={selected}
        placeholder={placeholder}
        onChange={handleChange}
        components={
          allowSelectAll && allSelected ? ALL_SELECTED_COMPONENTS : undefined
        }
        allSelectedLabel={`${strings.all_selected} (${options.length})`}
        onClearAll={() => onChange([])}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
      />
    </Field>
  );
};

/**
 * A day, picked from a calendar that can refuse days outright.
 *
 * `isValidDate` is react-datetime's own hook: the days it turns down are
 * rendered greyed and cannot be clicked, which is how the closed days of a
 * garage are kept out of reach rather than rejected after the fact.
 */
export const DateField = ({
  label,
  value,
  onChange,
  isValidDate,
  viewDate,
  required = false,
  help,
  error,
  placeholder = "YYYY-MM-DD",
}) => (
  <Field label={label} required={required} help={help} error={error}>
    <Datetime
      value={value}
      viewDate={viewDate}
      timeFormat={false}
      dateFormat="YYYY-MM-DD"
      closeOnSelect
      isValidDate={isValidDate}
      inputProps={{ placeholder, readOnly: true }}
      onChange={onChange}
    />
  </Field>
);

export const TimeField = ({
  label,
  value,
  onChange,
  required = false,
  help,
  error,
  placeholder = "HH:mm",
}) => (
  <Field label={label} required={required} help={help} error={error}>
    <Datetime
      value={value}
      dateFormat={false}
      timeFormat="HH:mm"
      inputProps={{ placeholder }}
      onChange={onChange}
    />
  </Field>
);

/**
 * What the api enforces on a password, checked here so the rules are visible
 * while typing instead of arriving as a 400 after saving.
 */
export const PASSWORD_RULES = [
  { key: "length", label: () => strings.pw_length, test: (v) => v.length >= 8 },
  { key: "upper", label: () => strings.pw_upper, test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: () => strings.pw_lower, test: (v) => /[a-z]/.test(v) },
  { key: "number", label: () => strings.pw_number, test: (v) => /[0-9]/.test(v) },
  {
    key: "symbol",
    label: () => strings.pw_symbol,
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

export const passwordIsValid = (value) =>
  PASSWORD_RULES.every((rule) => rule.test(value || ""));

export const PasswordField = ({
  label = strings.password,
  value = "",
  onChange,
  required = false,
  help,
  error,
  autoComplete = "new-password",
}) => {
  const typed = `${value}`.length > 0;

  return (
    <Field label={label} required={required} help={!typed ? help : ""} error={error}>
      <input
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      {typed && (
        <ul className="mpassword__rules">
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.test(`${value}`);
            return (
              <li key={rule.key} className={ok ? "is-ok" : ""}>
                <i
                  className={`nc-icon ${
                    ok ? "nc-check-2" : "nc-simple-remove"
                  }`}
                />
                {rule.label()}
              </li>
            );
          })}
        </ul>
      )}
    </Field>
  );
};

export const SwitchField = ({ label, help, checked, onChange }) => (
  <label className={`mswitch${checked ? " mswitch--on" : ""}`}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="mswitch__track">
      <span className="mswitch__thumb" />
    </span>
    <span className="mswitch__text">
      <span className="mswitch__label">{label}</span>
      {help && <span className="mswitch__help">{help}</span>}
    </span>
  </label>
);

export const FormAlert = ({ children }) =>
  children ? (
    <div className="mform__alert">
      <i className="nc-icon nc-alert-circle-i" />
      <span>{children}</span>
    </div>
  ) : null;

export const FormActions = ({
  onCancel,
  loading,
  saveLabel = strings.save,
  // For a form whose one required answer is missing: the button says so by
  // being unavailable, rather than the form only refusing once it is pressed.
  disabled = false,
}) => (
  <div className="mform__footer">
    {onCancel && (
      <button type="button" className="mform__cancel" onClick={onCancel}>
        {strings.cancel}
      </button>
    )}
    <ButtonLoader
      color="primary"
      loading={loading}
      disabled={disabled}
      type="submit"
    >
      {saveLabel}
    </ButtonLoader>
  </div>
);

// Dialog shell shared by every garage form
export const FormModal = ({
  isOpen,
  title,
  subtitle,
  icon = "nc-icon nc-settings-gear-65",
  size = "lg",
  onClose,
  children,
}) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    size={size}
    className="mmodal"
  >
    <ModalHeader toggle={onClose}>
      <span className="mmodal__title">
        <span className="mmodal__icon">
          <i className={icon} />
        </span>
        <span>
          <span className="mmodal__heading">{title}</span>
          {subtitle && <span className="mmodal__subtitle">{subtitle}</span>}
        </span>
      </span>
    </ModalHeader>
    <ModalBody>{isOpen && children}</ModalBody>
  </Modal>
);
